import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
  HttpContextToken,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastService } from './services/toast.service';
import { TranslateService } from '@ngx-translate/core';

export const SUPPRESS_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private toastService: ToastService,
    private translate: TranslateService
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (request.context.get(SUPPRESS_ERROR_TOAST)) return throwError(() => error);

        const serviceResult: any = error.error;
        const failDetails = serviceResult?.fail;
        const statusCode = error?.status;

        // 401 Unauthorized
        if ((failDetails && failDetails.status === 401) || statusCode === 401) {
          return throwError(() => error);
        }

        // 400 Validation Errors
        if (
          failDetails &&
          failDetails.status === 400 &&
          failDetails.detail ===
            'Please check the errors property for more details'
        ) {
          let errorMessages: string[] = [];

          for (const key in failDetails) {
            if (Array.isArray(failDetails[key])) {
              failDetails[key].forEach((err: string) => {
                errorMessages.push(err);
              });
            }
          }

          const validationMessage =
            errorMessages.length > 0
              ? errorMessages.join(', ')
              : this.translate.instant('error.validationError');

          this.toastService.showToast(validationMessage, 'warning');
          return throwError(() => error);
        }

        // 403 Forbidden
        if (serviceResult && serviceResult.status === 403) {
          const forbiddenMessage =
            failDetails?.title || this.translate.instant('error.forbidden');
          this.toastService.showToast(forbiddenMessage, 'error');
          return throwError(() => error);
        }

        // 500+ Server Errors
        if (
          failDetails &&
          failDetails.status >= 500 &&
          failDetails.status < 600
        ) {
          const serverErrorMessage =
            failDetails?.title || this.translate.instant('error.serverError');
          this.toastService.showToast(serverErrorMessage, 'error');
          return throwError(() => error);
        }

        // Generic error handling
        let errorMessage: string = '';

        if (failDetails?.title) {
          errorMessage = this.translate.instant(failDetails.title);
        } else if (serviceResult?.Errors?.length) {
          const errorMessageArr = serviceResult.Errors.map(
            (e: any) => e.Errors || e
          );
          errorMessage = errorMessageArr.join(' ');
        } else if (serviceResult?.detail) {
          errorMessage = serviceResult.detail;
        } else if (serviceResult?.message) {
          errorMessage = serviceResult.message;
        } else {
          // Fallback to status code messages
          switch (statusCode) {
            case 400:
              errorMessage = this.translate.instant('error.badRequest');
              break;
            case 403:
              errorMessage = this.translate.instant('error.forbidden');
              break;
            case 404:
              errorMessage = this.translate.instant('error.notFound');
              break;
            case 405:
              errorMessage = this.translate.instant('error.methodNotAllowed');
              break;
            case 500:
              errorMessage = this.translate.instant('error.serverError');
              break;
            default:
              errorMessage = this.translate.instant('error.errorOccured');
              break;
          }
        }

        if (errorMessage) {
          this.toastService.showToast(errorMessage, 'error');
        }

        return throwError(() => error);
      })
    );
  }
}
